export const DEFAULT_LANGUAGE = 'us'

export const LANGUAGE_OPTIONS = [
  { id: 'zh', label: 'Chinese (\u7b80\u4f53\u4e2d\u6587)' },
  { id: 'tw', label: 'Chinese (\u7e41\u9ad4\u4e2d\u6587)' },
  { id: 'nl', label: 'Dutch (Nederlands)' },
  { id: 'uk', label: 'English (English UK)' },
  { id: 'us', label: 'English (English US)' },
  { id: 'fr', label: 'French (Fran\u00e7ais)' },
  { id: 'de', label: 'German (Deutsch)' },
  { id: 'it', label: 'Italian (Italiano)' },
  { id: 'ja', label: 'Japanese (\u65e5\u672c\u8a9e)' },
  { id: 'ko', label: 'Korean (\ud55c\uad6d\uc5b4)' },
  { id: 'pt', label: 'Portuguese (Portugu\u00eas)' },
  { id: 'ru', label: 'Russian (\u0420\u0443\u0441\u0441\u043a\u0438\u0439)' },
  { id: 'es', label: 'Spanish (Espa\u00f1ol)' }
]

export const SUPPORTED_LANGUAGES = LANGUAGE_OPTIONS.map(item => item.id)

const LANGUAGE_ALIASES = {
  en: 'us',
  'en-us': 'us',
  us: 'us',
  'en-gb': 'uk',
  gb: 'uk',
  cn: 'zh',
  'zh-cn': 'zh',
  'zh-hans': 'zh',
  'zh-tw': 'tw',
  'zh-hant': 'tw'
}

export const APP_NAMES = {
  paper: 'Paper',
  meeting: 'Meeting',
  learning: 'Learning',
  memo: 'Memo',
  daily: 'Daily',
  knowledge: 'Knowledge Base',
  'knowledge base': 'Knowledge Base'
}

export function normalizeLanguage(value) {
  const key = String(value || '').trim().toLowerCase().replace('_', '-')
  const language = LANGUAGE_ALIASES[key] || key
  return SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE
}

export function getLanguageOption(value) {
  const language = normalizeLanguage(value)
  return LANGUAGE_OPTIONS.find(item => item.id == language) || LANGUAGE_OPTIONS.find(item => item.id == DEFAULT_LANGUAGE)
}

export function mergeLanguageText(textMap, language) {
  const normalized = normalizeLanguage(language)
  return Object.assign({}, textMap[DEFAULT_LANGUAGE] || {}, textMap[normalized] || {})
}

export function getAppName(value) {
  const key = String(value || '').trim().toLowerCase()
  return APP_NAMES[key] || ''
}

export const APP_UI_TEXTS = {
  us: {
    myFiles: 'My Files',
    all: 'All',
    favorite: 'Favorites',
    recentDeleted: 'Recently deleted',
    search: 'Search',
    createFolder: 'New folder',
    importPdf: 'Import PDF',
    modifyTime: 'Modified',
    createTime: 'Created',
    name: 'Name',
    desc: 'Descending',
    asc: 'Ascending',
    listView: 'List view',
    gridView: 'Grid view',
    empty: 'No data',
    item: 'item',
    page: 'page',
    selectedPrefix: 'Selected',
    selectedSuffix: 'items',
    cancel: 'Cancel',
    ok: 'OK',
    more: 'More',
    download: 'Download',
    rename: 'Rename',
    move: 'Move',
    encrypt: 'Encrypt',
    decrypt: 'Decrypt',
    encrypted: 'Encrypted',
    unfavorite: 'Remove favorite',
    delete: 'Delete',
    back: 'Back',
    recordings: 'Recordings',
    allRecordings: 'All recordings',
    transcript: 'Transcript',
    pageGrid: 'Page grid',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    close: 'Close',
    expand: 'Expand',
    collapse: 'Collapse',
    open: 'Open',
    clear: 'Clear',
    downloadList: 'Downloads',
    chooseFormat: 'Choose download format',
    notice: 'Notice',
    loading: 'Loading',
    loadFailed: 'Failed to load',
    detailLoadFailed: 'Failed to parse. Please try again.',
    dataLoadFailed: '{app} data failed to load. Please try again later.',
    retry: 'Retry',
    passwordTitle: 'Enter password',
    passwordPlaceholder: 'Please enter the password',
    passwordHide: 'Hide password',
    passwordShow: 'Show password',
    passwordConfirming: 'Verifying',
    passwordRequired: 'Please enter the password',
    passwordWrong: 'Incorrect password. Please try again.',
    verifying: 'Verifying',
    readingProgress: 'Reading progress',
    noReadingProgress: 'No reading progress',
    bookmarks: 'Bookmarks',
    highlights: 'Highlights',
    annotations: 'Handwritten notes',
    noBookmarks: 'No bookmarks',
    noHighlights: 'No highlights',
    noAnnotations: 'No handwritten notes',
    noTranscript: 'No transcript available.',
    transcriptLoading: 'Transcript loading...',
    transcriptPending: 'The transcript file has been returned. Its content will be shown after the official API is ready.',
    chooseDownloadableFile: 'Please select a downloadable file',
    foldersNotDownloadable: 'Folders and templates cannot be downloaded. Please select a file',
    downloadPreparing: 'Preparing download',
    downloading: 'Downloading',
    downloadDone: 'Download complete',
    downloadFailed: 'Download failed',
    savingFile: 'Saving file',
    generatingPdf: 'Generating PDF',
    packingZip: 'Packing ZIP',
    continueDownload: 'Continue download',
    imageReadFailed: 'Image read failed. Click to retry',
    maxBatchDownload: 'Only up to {count} files can be downloaded at once.',
    maxRunningDownload: 'Only up to {count} downloads can run at the same time. Please wait for some tasks to finish.',
    fakeTip: 'This API is not available yet.',
    today: 'Today',
    todo: 'To-do',
    tasks: 'Tasks',
    notes: 'Notes',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    allDay: 'All day',
    allStatus: 'All status',
    finished: 'Finished',
    unfinished: 'Unfinished',
    dailyDateFormat: '{year}-{month}-{day}',
    yearFormat: '{year}',
    monthFormat: '{month}',
    weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  },
  uk: {},
  zh: {
    myFiles: '\u6211\u7684\u6587\u4ef6',
    all: '\u5168\u90e8',
    favorite: '\u6536\u85cf',
    recentDeleted: '\u6700\u8fd1\u5220\u9664',
    search: '\u641c\u7d22',
    createFolder: '\u65b0\u5efa\u6587\u4ef6\u5939',
    importPdf: '\u5bfc\u5165 PDF',
    modifyTime: '\u66f4\u65b0\u65f6\u95f4',
    createTime: '\u521b\u5efa\u65f6\u95f4',
    name: '\u540d\u79f0',
    desc: '\u5012\u5e8f',
    asc: '\u6b63\u5e8f',
    listView: '\u5217\u8868',
    gridView: '\u7f51\u683c',
    empty: '\u6682\u65e0\u6570\u636e',
    item: '\u9879',
    page: '\u9875',
    selectedPrefix: '\u5df2\u9009\u62e9',
    selectedSuffix: '\u9879',
    cancel: '\u53d6\u6d88',
    ok: '\u786e\u5b9a',
    more: '\u66f4\u591a',
    download: '\u4e0b\u8f7d',
    rename: '\u91cd\u547d\u540d',
    move: '\u79fb\u52a8',
    encrypt: '\u52a0\u5bc6',
    decrypt: '\u53d6\u6d88\u52a0\u5bc6',
    encrypted: '\u5df2\u52a0\u5bc6',
    unfavorite: '\u53d6\u6d88\u6536\u85cf',
    delete: '\u5220\u9664',
    back: '\u8fd4\u56de',
    recordings: '\u5f55\u97f3',
    allRecordings: '\u5168\u90e8\u5f55\u97f3',
    transcript: '\u5f55\u97f3\u8f6c\u6587\u672c',
    pageGrid: '\u9875\u9762\u5217\u8868',
    zoomIn: '\u653e\u5927',
    zoomOut: '\u7f29\u5c0f',
    close: '\u5173\u95ed',
    expand: '\u5c55\u5f00',
    collapse: '\u6536\u8d77',
    open: '\u6253\u5f00',
    clear: '\u6e05\u9664',
    downloadList: '\u4e0b\u8f7d\u5217\u8868',
    chooseFormat: '\u9009\u62e9\u4e0b\u8f7d\u683c\u5f0f',
    notice: '\u63d0\u793a',
    loading: '\u52a0\u8f7d\u4e2d',
    loadFailed: '\u52a0\u8f7d\u5931\u8d25',
    detailLoadFailed: '\u89e3\u6790\u5931\u8d25\uff0c\u8bf7\u91cd\u8bd5',
    dataLoadFailed: '{app} \u6570\u636e\u52a0\u8f7d\u5931\u8d25\uff0c\u8bf7\u7a0d\u540e\u91cd\u8bd5',
    retry: '\u91cd\u8bd5',
    passwordTitle: '\u8f93\u5165\u5bc6\u7801',
    passwordPlaceholder: '\u8bf7\u8f93\u5165\u5bc6\u7801',
    passwordHide: '\u9690\u85cf\u5bc6\u7801',
    passwordShow: '\u663e\u793a\u5bc6\u7801',
    passwordConfirming: '\u9a8c\u8bc1\u4e2d',
    passwordRequired: '\u8bf7\u8f93\u5165\u5bc6\u7801',
    passwordWrong: '\u5bc6\u7801\u9519\u8bef\uff0c\u8bf7\u91cd\u65b0\u8f93\u5165\u5bc6\u7801\uff01',
    verifying: '\u9a8c\u8bc1\u4e2d',
    readingProgress: '\u9605\u8bfb\u8fdb\u5ea6',
    noReadingProgress: '\u6682\u65e0\u9605\u8bfb\u8fdb\u5ea6',
    bookmarks: '\u4e66\u7b7e',
    highlights: '\u9ad8\u4eae',
    annotations: '\u624b\u5199\u6279\u6ce8',
    noBookmarks: '\u6682\u65e0\u4e66\u7b7e',
    noHighlights: '\u6682\u65e0\u9ad8\u4eae',
    noAnnotations: '\u6682\u65e0\u624b\u5199\u6279\u6ce8',
    noTranscript: '\u6682\u65e0\u5f55\u97f3\u6587\u672c\u3002',
    transcriptLoading: '\u8f6c\u5199\u6587\u672c\u52a0\u8f7d\u4e2d...',
    transcriptPending: '\u8be5\u5f55\u97f3\u7684\u6587\u672c\u6587\u4ef6\u5730\u5740\u5df2\u8fd4\u56de\uff0c\u6587\u672c\u5185\u5bb9\u5c06\u5728\u6b63\u5f0f\u63a5\u53e3\u8865\u9f50\u540e\u5c55\u793a\u3002',
    chooseDownloadableFile: '\u8bf7\u9009\u62e9\u53ef\u4e0b\u8f7d\u7684\u6587\u4ef6',
    foldersNotDownloadable: '\u6587\u4ef6\u5939\u548c\u6a21\u677f\u4e0d\u652f\u6301\u4e0b\u8f7d\uff0c\u8bf7\u9009\u62e9\u6587\u4ef6',
    downloadPreparing: '\u51c6\u5907\u4e0b\u8f7d',
    downloading: '\u4e0b\u8f7d\u4e2d',
    downloadDone: '\u4e0b\u8f7d\u5b8c\u6210',
    downloadFailed: '\u4e0b\u8f7d\u5931\u8d25',
    savingFile: '\u4fdd\u5b58\u6587\u4ef6',
    generatingPdf: '\u751f\u6210 PDF',
    packingZip: '\u6253\u5305 ZIP',
    continueDownload: '\u7ee7\u7eed\u4e0b\u8f7d',
    imageReadFailed: '\u56fe\u7247\u8bfb\u53d6\u5931\u8d25\uff0c\u8bf7\u70b9\u51fb\u91cd\u8bd5',
    maxBatchDownload: '\u4e00\u6b21\u6700\u591a\u652f\u6301\u4e0b\u8f7d {count} \u4e2a\u6587\u4ef6',
    maxRunningDownload: '\u5f53\u524d\u6700\u591a\u53ea\u80fd\u540c\u65f6\u4e0b\u8f7d {count} \u4e2a\u4efb\u52a1\uff0c\u8bf7\u7b49\u5f85\u90e8\u5206\u4efb\u52a1\u5b8c\u6210\u540e\u518d\u4e0b\u8f7d',
    fakeTip: '\u63a5\u53e3\u6682\u672a\u5f00\u653e\uff0c\u5df2\u7528\u5047\u63a5\u53e3\u5360\u4f4d',
    today: '\u4eca\u5929',
    todo: '\u5f85\u529e',
    tasks: '\u4efb\u52a1',
    notes: '\u7b14\u8bb0',
    month: '\u6708',
    week: '\u5468',
    day: '\u65e5',
    allDay: '\u5168\u90e8\u65e5\u671f',
    allStatus: '\u5168\u90e8\u72b6\u6001',
    finished: '\u5df2\u5b8c\u6210',
    unfinished: '\u672a\u5b8c\u6210',
    dailyDateFormat: '{year} \u5e74 {month} \u6708 {day} \u65e5',
    yearFormat: '{year} \u5e74',
    monthFormat: '{month} \u6708',
    weekdays: ['\u65e5', '\u4e00', '\u4e8c', '\u4e09', '\u56db', '\u4e94', '\u516d']
  },
  tw: {
    myFiles: '\u6211\u7684\u6a94\u6848',
    all: '\u5168\u90e8',
    favorite: '\u6536\u85cf',
    recentDeleted: '\u6700\u8fd1\u522a\u9664',
    search: '\u641c\u5c0b',
    createFolder: '\u65b0\u589e\u8cc7\u6599\u593e',
    importPdf: '\u532f\u5165 PDF',
    modifyTime: '\u66f4\u65b0\u6642\u9593',
    createTime: '\u5efa\u7acb\u6642\u9593',
    name: '\u540d\u7a31',
    desc: '\u5012\u5e8f',
    asc: '\u6b63\u5e8f',
    listView: '\u5217\u8868',
    gridView: '\u7db2\u683c',
    empty: '\u66ab\u7121\u8cc7\u6599',
    item: '\u9805',
    page: '\u9801',
    selectedPrefix: '\u5df2\u9078\u64c7',
    selectedSuffix: '\u9805',
    cancel: '\u53d6\u6d88',
    ok: '\u78ba\u5b9a',
    more: '\u66f4\u591a',
    download: '\u4e0b\u8f09',
    rename: '\u91cd\u65b0\u547d\u540d',
    move: '\u79fb\u52d5',
    encrypt: '\u52a0\u5bc6',
    decrypt: '\u53d6\u6d88\u52a0\u5bc6',
    encrypted: '\u5df2\u52a0\u5bc6',
    unfavorite: '\u53d6\u6d88\u6536\u85cf',
    delete: '\u522a\u9664',
    back: '\u8fd4\u56de',
    recordings: '\u9304\u97f3',
    allRecordings: '\u5168\u90e8\u9304\u97f3',
    transcript: '\u9304\u97f3\u8f49\u6587\u672c',
    pageGrid: '\u9801\u9762\u5217\u8868',
    zoomIn: '\u653e\u5927',
    zoomOut: '\u7e2e\u5c0f',
    close: '\u95dc\u9589',
    expand: '\u5c55\u958b',
    collapse: '\u6536\u8d77',
    open: '\u6253\u958b',
    clear: '\u6e05\u9664',
    downloadList: '\u4e0b\u8f09\u5217\u8868',
    chooseFormat: '\u9078\u64c7\u4e0b\u8f09\u683c\u5f0f',
    notice: '\u63d0\u793a',
    loading: '\u52a0\u8f09\u4e2d',
    loadFailed: '\u52a0\u8f09\u5931\u6557',
    detailLoadFailed: '\u89e3\u6790\u5931\u6557\uff0c\u8acb\u91cd\u8a66',
    dataLoadFailed: '{app} \u8cc7\u6599\u52a0\u8f09\u5931\u6557\uff0c\u8acb\u7a0d\u5f8c\u91cd\u8a66',
    retry: '\u91cd\u8a66',
    passwordTitle: '\u8f38\u5165\u5bc6\u78bc',
    passwordPlaceholder: '\u8acb\u8f38\u5165\u5bc6\u78bc',
    passwordHide: '\u96b1\u85cf\u5bc6\u78bc',
    passwordShow: '\u986f\u793a\u5bc6\u78bc',
    passwordConfirming: '\u9a57\u8b49\u4e2d',
    passwordRequired: '\u8acb\u8f38\u5165\u5bc6\u78bc',
    passwordWrong: '\u5bc6\u78bc\u932f\u8aa4\uff0c\u8acb\u91cd\u65b0\u8f38\u5165\uff01',
    verifying: '\u9a57\u8b49\u4e2d',
    readingProgress: '\u95b1\u8b80\u9032\u5ea6',
    noReadingProgress: '\u66ab\u7121\u95b1\u8b80\u9032\u5ea6',
    bookmarks: '\u66f8\u7c64',
    highlights: '\u9ad8\u4eae',
    annotations: '\u624b\u5beb\u6279\u8a3b',
    noBookmarks: '\u66ab\u7121\u66f8\u7c64',
    noHighlights: '\u66ab\u7121\u9ad8\u4eae',
    noAnnotations: '\u66ab\u7121\u624b\u5beb\u6279\u8a3b',
    noTranscript: '\u66ab\u7121\u9304\u97f3\u6587\u672c\u3002',
    transcriptLoading: '\u8f49\u5beb\u6587\u672c\u52a0\u8f09\u4e2d...',
    transcriptPending: '\u8a72\u9304\u97f3\u7684\u6587\u672c\u6a94\u6848\u4f4d\u5740\u5df2\u8fd4\u56de\uff0c\u6587\u672c\u5167\u5bb9\u5c07\u5728\u6b63\u5f0f\u63a5\u53e3\u88dc\u9f4a\u5f8c\u986f\u793a\u3002',
    chooseDownloadableFile: '\u8acb\u9078\u64c7\u53ef\u4e0b\u8f09\u7684\u6a94\u6848',
    foldersNotDownloadable: '\u8cc7\u6599\u593e\u548c\u6a21\u677f\u4e0d\u652f\u63f4\u4e0b\u8f09\uff0c\u8acb\u9078\u64c7\u6a94\u6848',
    downloadPreparing: '\u6e96\u5099\u4e0b\u8f09',
    downloading: '\u4e0b\u8f09\u4e2d',
    downloadDone: '\u4e0b\u8f09\u5b8c\u6210',
    downloadFailed: '\u4e0b\u8f09\u5931\u6557',
    savingFile: '\u5132\u5b58\u6a94\u6848',
    generatingPdf: '\u7522\u751f PDF',
    packingZip: '\u6253\u5305 ZIP',
    continueDownload: '\u7e7c\u7e8c\u4e0b\u8f09',
    imageReadFailed: '\u5716\u7247\u8b80\u53d6\u5931\u6557\uff0c\u8acb\u9ede\u64ca\u91cd\u8a66',
    maxBatchDownload: '\u4e00\u6b21\u6700\u591a\u652f\u63f4\u4e0b\u8f09 {count} \u500b\u6a94\u6848',
    maxRunningDownload: '\u76ee\u524d\u6700\u591a\u53ea\u80fd\u540c\u6642\u4e0b\u8f09 {count} \u500b\u4efb\u52d9\uff0c\u8acb\u7b49\u5f85\u90e8\u5206\u4efb\u52d9\u5b8c\u6210\u5f8c\u518d\u4e0b\u8f09',
    today: '\u4eca\u5929',
    todo: '\u5f85\u8fa6',
    tasks: '\u4efb\u52d9',
    notes: '\u7b46\u8a18',
    month: '\u6708',
    week: '\u9031',
    day: '\u65e5',
    allDay: '\u5168\u90e8\u65e5\u671f',
    allStatus: '\u5168\u90e8\u72c0\u614b',
    finished: '\u5df2\u5b8c\u6210',
    unfinished: '\u672a\u5b8c\u6210',
    dailyDateFormat: '{year} \u5e74 {month} \u6708 {day} \u65e5',
    yearFormat: '{year} \u5e74',
    monthFormat: '{month} \u6708',
    weekdays: ['\u65e5', '\u4e00', '\u4e8c', '\u4e09', '\u56db', '\u4e94', '\u516d']
  },
  nl: {
    myFiles: 'Mijn bestanden',
    all: 'Alles',
    favorite: 'Favorieten',
    recentDeleted: 'Onlangs verwijderd',
    search: 'Zoeken',
    download: 'Downloaden',
    cancel: 'Annuleren',
    ok: 'OK',
    close: 'Sluiten',
    empty: 'Geen gegevens',
    loading: 'Laden',
    retry: 'Opnieuw',
    back: 'Terug',
    chooseFormat: 'Downloadformaat kiezen',
    today: 'Vandaag',
    weekdays: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za']
  },
  fr: {
    myFiles: 'Mes fichiers',
    all: 'Tout',
    favorite: 'Favoris',
    recentDeleted: 'Supprim\u00e9s r\u00e9cemment',
    search: 'Rechercher',
    download: 'T\u00e9l\u00e9charger',
    cancel: 'Annuler',
    ok: 'OK',
    close: 'Fermer',
    empty: 'Aucune donn\u00e9e',
    loading: 'Chargement',
    retry: 'R\u00e9essayer',
    back: 'Retour',
    chooseFormat: 'Choisir le format',
    today: 'Aujourd\u2019hui',
    weekdays: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
  },
  de: {
    myFiles: 'Meine Dateien',
    all: 'Alle',
    favorite: 'Favoriten',
    recentDeleted: 'K\u00fcrzlich gel\u00f6scht',
    search: 'Suchen',
    download: 'Herunterladen',
    cancel: 'Abbrechen',
    ok: 'OK',
    close: 'Schlie\u00dfen',
    empty: 'Keine Daten',
    loading: 'Wird geladen',
    retry: 'Erneut versuchen',
    back: 'Zur\u00fcck',
    chooseFormat: 'Downloadformat w\u00e4hlen',
    today: 'Heute',
    weekdays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
  },
  it: {
    myFiles: 'I miei file',
    all: 'Tutto',
    favorite: 'Preferiti',
    recentDeleted: 'Eliminati di recente',
    search: 'Cerca',
    download: 'Scarica',
    cancel: 'Annulla',
    ok: 'OK',
    close: 'Chiudi',
    empty: 'Nessun dato',
    loading: 'Caricamento',
    retry: 'Riprova',
    back: 'Indietro',
    chooseFormat: 'Scegli formato',
    today: 'Oggi',
    weekdays: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab']
  },
  ja: {
    myFiles: '\u30de\u30a4\u30d5\u30a1\u30a4\u30eb',
    all: '\u3059\u3079\u3066',
    favorite: '\u304a\u6c17\u306b\u5165\u308a',
    recentDeleted: '\u6700\u8fd1\u524a\u9664',
    search: '\u691c\u7d22',
    download: '\u30c0\u30a6\u30f3\u30ed\u30fc\u30c9',
    cancel: '\u30ad\u30e3\u30f3\u30bb\u30eb',
    ok: 'OK',
    close: '\u9589\u3058\u308b',
    empty: '\u30c7\u30fc\u30bf\u306a\u3057',
    loading: '\u8aad\u307f\u8fbc\u307f\u4e2d',
    retry: '\u518d\u8a66\u884c',
    back: '\u623b\u308b',
    chooseFormat: '\u30c0\u30a6\u30f3\u30ed\u30fc\u30c9\u5f62\u5f0f\u3092\u9078\u629e',
    today: '\u4eca\u65e5',
    weekdays: ['\u65e5', '\u6708', '\u706b', '\u6c34', '\u6728', '\u91d1', '\u571f']
  },
  ko: {
    myFiles: '\ub0b4 \ud30c\uc77c',
    all: '\uc804\uccb4',
    favorite: '\uc990\uaca8\ucc3e\uae30',
    recentDeleted: '\ucd5c\uadfc \uc0ad\uc81c',
    search: '\uac80\uc0c9',
    download: '\ub2e4\uc6b4\ub85c\ub4dc',
    cancel: '\ucde8\uc18c',
    ok: '\ud655\uc778',
    close: '\ub2eb\uae30',
    empty: '\ub370\uc774\ud130 \uc5c6\uc74c',
    loading: '\ub85c\ub4dc \uc911',
    retry: '\ub2e4\uc2dc \uc2dc\ub3c4',
    back: '\ub4a4\ub85c',
    chooseFormat: '\ub2e4\uc6b4\ub85c\ub4dc \ud615\uc2dd \uc120\ud0dd',
    today: '\uc624\ub298',
    weekdays: ['\uc77c', '\uc6d4', '\ud654', '\uc218', '\ubaa9', '\uae08', '\ud1a0']
  },
  pt: {
    myFiles: 'Meus arquivos',
    all: 'Tudo',
    favorite: 'Favoritos',
    recentDeleted: 'Exclu\u00eddos recentemente',
    search: 'Pesquisar',
    download: 'Baixar',
    cancel: 'Cancelar',
    ok: 'OK',
    close: 'Fechar',
    empty: 'Sem dados',
    loading: 'Carregando',
    retry: 'Tentar novamente',
    back: 'Voltar',
    chooseFormat: 'Escolher formato',
    today: 'Hoje',
    weekdays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'S\u00e1b']
  },
  ru: {
    myFiles: '\u041c\u043e\u0438 \u0444\u0430\u0439\u043b\u044b',
    all: '\u0412\u0441\u0435',
    favorite: '\u0418\u0437\u0431\u0440\u0430\u043d\u043d\u043e\u0435',
    recentDeleted: '\u041d\u0435\u0434\u0430\u0432\u043d\u043e \u0443\u0434\u0430\u043b\u0435\u043d\u043d\u044b\u0435',
    search: '\u041f\u043e\u0438\u0441\u043a',
    download: '\u0421\u043a\u0430\u0447\u0430\u0442\u044c',
    cancel: '\u041e\u0442\u043c\u0435\u043d\u0430',
    ok: 'OK',
    close: '\u0417\u0430\u043a\u0440\u044b\u0442\u044c',
    empty: '\u041d\u0435\u0442 \u0434\u0430\u043d\u043d\u044b\u0445',
    loading: '\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430',
    retry: '\u041f\u043e\u0432\u0442\u043e\u0440\u0438\u0442\u044c',
    back: '\u041d\u0430\u0437\u0430\u0434',
    chooseFormat: '\u0412\u044b\u0431\u0440\u0430\u0442\u044c \u0444\u043e\u0440\u043c\u0430\u0442',
    today: '\u0421\u0435\u0433\u043e\u0434\u043d\u044f',
    weekdays: ['\u0412\u0441', '\u041f\u043d', '\u0412\u0442', '\u0421\u0440', '\u0427\u0442', '\u041f\u0442', '\u0421\u0431']
  },
  es: {
    myFiles: 'Mis archivos',
    all: 'Todo',
    favorite: 'Favoritos',
    recentDeleted: 'Eliminados recientemente',
    search: 'Buscar',
    download: 'Descargar',
    cancel: 'Cancelar',
    ok: 'OK',
    close: 'Cerrar',
    empty: 'Sin datos',
    loading: 'Cargando',
    retry: 'Reintentar',
    back: 'Atr\u00e1s',
    chooseFormat: 'Elegir formato',
    today: 'Hoy',
    weekdays: ['Dom', 'Lun', 'Mar', 'Mi\u00e9', 'Jue', 'Vie', 'S\u00e1b']
  }
}

export function getAppUiText(language) {
  return mergeLanguageText(APP_UI_TEXTS, language)
}
